import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback';
import { useUpdateCriteria } from '@/services/apis/mutations/lot-sizing/use-update-criteria';
import { useGetLotSizingCriteria } from '@/services/apis/queries/lot-sizing/use-get-lot-sizing-criteria';
import { LotSizingSizingMethod } from '@/types/core/lot-sizing/lot-sizing-sizing-method';
import type { LotSizingFormValues } from '../schema/criteria';
import { lotSizingSchema } from '../schema/criteria';
import { useCriteriaOptions } from './use-criteria-options';

type UseCriteriaFormParams = {
  lotSizingId: string;
};

export function useCriteriaForm({ lotSizingId }: UseCriteriaFormParams) {
  const { data: criteria } = useGetLotSizingCriteria(lotSizingId);
  const { mutate } = useUpdateCriteria({ lotSizingId });
  const { getContainerSelection } = useCriteriaOptions();
  const containerSelection = getContainerSelection(criteria);

  const { control, formState } = useForm<LotSizingFormValues>({
    resolver: zodResolver(lotSizingSchema),
    values: {
      contenantType: 'volume',
      containerSelection: containerSelection.value,
      volumeMaximum: criteria.volumeTarget,
    },
  });

  const canEditMaximumVolume =
    criteria.sizingMethod === LotSizingSizingMethod.PERSONALISED;
  const isDefaultContainerVolume = criteria.isDefaultContainerVolume;
  const containerSelectionOptions = containerSelection.options;

  const onVolumeMaximumChange = useDebouncedCallback(
    (value: number | string) => {
      const volumeMaximum = Number(value);

      if (
        !canEditMaximumVolume ||
        volumeMaximum < 1 ||
        Number.isNaN(volumeMaximum)
      ) {
        return;
      }
      if (volumeMaximum === criteria.volumeTarget) {
        return;
      }

      mutate({
        id: lotSizingId,
        sizingMethod:
          criteria.sizingMethod === LotSizingSizingMethod.PERSONALISED
            ? LotSizingSizingMethod.PERSONALISED
            : LotSizingSizingMethod.STANDARD,
        containerId: criteria.containerId,
        volumeTarget: volumeMaximum,
      });
    },
    1000,
  );

  return {
    control,
    formState,
    containerSelectionOptions,
    canEditMaximumVolume,
    isDefaultContainerVolume,
    onVolumeMaximumChange,
  };
}
